import _ from 'underscore';

import {assert} from 'Asserts';
import {takeContainedServerIdsFromClipboard} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {isNoteLinkedFile} from 'notebook/TypePredicates';
import {Note, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';

import {EditorMixinInterface} from '../Editor';
import convertStructuredTableForPaste from '../structured-tables/convertStructuredTableForPaste';

export default function convertStoichiometryGroupForPaste(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any,
  name: string | null,
  component: Partial<EditorMixinInterface>,
  usedTableNames: Array<string>,
  destinationFileId: string,
  isConfig: boolean | undefined,
  note?: Note
): void {
  assert(name != null, 'Stoichiometry group paste requires a name to avoid duplicate table name conflicts');

  // Clear the reaction link — the pasted copy is independent from the original reaction.
  item.item.groupMetadata = {reactionId: null};

  // Server ids for the contained reaction-scheme file are smuggled through the clipboard by
  // NoteLinkedGroupPlugin so a cross-entry paste can resolve them (getIdFromCid only knows the
  // destination entry's cidIdMap). takeContainedServerIdsFromClipboard always strips the transient
  // field (so it never reaches the store or serialization) even when no scheme was smuggled.
  const containedServerIdsFromClipboard = takeContainedServerIdsFromClipboard(item);
  const newCidToServerId: CidIdMap = {};

  for (let i = 0; i < item.item.containedNoteParts.length; i++) {
    const containedNotePart = item.item.containedNoteParts[i];
    const containedItem = containedNotePart.item;
    if (component.props!.getType(containedItem) === NoteLinkedObjectType.STRUCTURED_TABLE) {
      assert(
        containedItem.kind === StructuredTableKind.STOICHIOMETRY,
        `Expected stoichiometry table but got ${containedItem.kind}`
      );
      const newContainedNotePart = convertStructuredTableForPaste(containedNotePart, {
        usedTableNames,
        destinationFileId,
        isDestinationConfig: isConfig,
        note,
      }).item;
      newContainedNotePart.item.name = name;
      newContainedNotePart.cid = _.uniqueId('note-part');
      item.item.containedNoteParts[i] = newContainedNotePart;
    } else if (containedItem != null && isNoteLinkedFile(containedItem)) {
      // The reaction-scheme NoteLinkedFile's cid is a server-assigned id. Regenerate it and
      // re-register its server id (mirrors convertNotePartGridForPaste): same-entry paste resolves
      // via the shared cidIdMap; cross-entry paste falls back to the clipboard-embedded map. Reusing
      // the same server id (rather than cloning the file server-side) matches the image paste contract.
      const oldItemCid = containedItem.cid;
      const serverId =
        component.props!.getIdFromCid(oldItemCid) ?? containedServerIdsFromClipboard?.[oldItemCid];
      const newItemCid = _.uniqueId('note-linked-file-');
      containedItem.cid = newItemCid;
      containedNotePart.cid = _.uniqueId('note-part');
      if (serverId != null) {
        newCidToServerId[newItemCid] = serverId;
      }
    }
  }

  if (!_.isEmpty(newCidToServerId)) {
    component.props!.registerObjects(newCidToServerId);
  }
}
